import { gql } from "@/gql";

export const INITIATE_GOOGLE_CONNECT_MUTATION = gql(`
  mutation InitiateGoogleConnect($salonId: ID!) {
    initiateGoogleConnect(salonId: $salonId) {
      authUrl
    }
  }
`);

export const PENDING_GOOGLE_CONNECTION_QUERY = gql(`
  query PendingGoogleConnection($salonId: ID!) {
    pendingGoogleConnection(salonId: $salonId) {
      locations {
        accountId
        accountName
        locationId
        locationName
        address
      }
    }
  }
`);

export const LINK_GOOGLE_LOCATION_MUTATION = gql(`
  mutation LinkGoogleLocation($input: LinkGoogleLocationInput!) {
    linkGoogleLocation(input: $input) {
      id
      salonId
      locationName
      placeId
      authMode
      managerEmail
      syncEnabled
      reauthRequired
      lastSyncAt
      lastSyncError
      lastLocationSyncAt
      lastLocationSyncError
      connectedAt
      otherSalonsUsingSameAccount {
        salonId
        salonName
      }
    }
  }
`);

export const DISCONNECT_GOOGLE_BUSINESS_MUTATION = gql(`
  mutation DisconnectGoogleBusiness($salonId: ID!) {
    disconnectGoogleBusiness(salonId: $salonId)
  }
`);

export const SET_GOOGLE_BUSINESS_SYNC_ENABLED_MUTATION = gql(`
  mutation SetGoogleBusinessSyncEnabled($salonId: ID!, $enabled: Boolean!) {
    setGoogleBusinessSyncEnabled(salonId: $salonId, enabled: $enabled) {
      id
      salonId
      syncEnabled
      lastSyncAt
      lastSyncError
      reauthRequired
    }
  }
`);

export const GOOGLE_BUSINESS_CONNECTION_QUERY = gql(`
  query GoogleBusinessConnection($salonId: ID!) {
    googleBusinessConnection(salonId: $salonId) {
      id
      salonId
      locationName
      placeId
      authMode
      managerEmail
      syncEnabled
      reauthRequired
      lastSyncAt
      lastSyncError
      lastLocationSyncAt
      lastLocationSyncError
      syncHoursToGoogle
      lastHoursPushAt
      lastHoursPushError
      syncHolidaysToGoogle
      lastHolidaysPushAt
      lastHolidaysPushError
      syncServicesToGoogle
      lastServicesPushAt
      lastServicesPushError
      connectedAt
      otherSalonsUsingSameAccount {
        salonId
        salonName
      }
    }
  }
`);

export const NOTIFY_ADMIN_OF_PENDING_GOOGLE_BUSINESS_INVITATION_MUTATION = gql(`
  mutation NotifyAdminOfPendingGoogleBusinessInvitation($salonId: ID!) {
    notifyAdminOfPendingGoogleBusinessInvitation(salonId: $salonId)
  }
`);

export const GOOGLE_BUSINESS_MANAGED_INFO_QUERY = gql(`
  query GoogleBusinessManagedInfo {
    googleBusinessManagedInfo {
      defaultManagerEmail
    }
  }
`);

export const GOOGLE_CONNECTION_HEALTH_QUERY = gql(`
  query GoogleConnectionHealth($salonId: ID!) {
    googleConnectionHealth(salonId: $salonId) {
      hasConnection
      authMode
      managerEmail
      lastSyncAt
      lastSyncError
      nextScheduledSyncAt
      syncEnabled
      reauthRequired
      managerReauthRequired
      managerActive
    }
  }
`);

export const GENERATE_GOOGLE_REVIEW_REPLY_MUTATION = gql(`
  mutation GenerateGoogleReviewReply($reviewId: ID!) {
    generateGoogleReviewReply(reviewId: $reviewId) {
      suggestedReply
    }
  }
`);

export const REPLY_TO_GOOGLE_REVIEW_MUTATION = gql(`
  mutation ReplyToGoogleReview($reviewId: ID!, $text: String!) {
    replyToGoogleReview(reviewId: $reviewId, text: $text) {
      reviewId
      replyComment
      replyTime
    }
  }
`);

export const DELETE_GOOGLE_REVIEW_REPLY_MUTATION = gql(`
  mutation DeleteGoogleReviewReply($reviewId: ID!) {
    deleteGoogleReviewReply(reviewId: $reviewId) {
      reviewId
      replyComment
      replyTime
    }
  }
`);

export const CREATE_GBP_POST_MUTATION = gql(`
  mutation CreateGbpPost($salonId: ID!, $input: CreateGbpPostInput!) {
    createGbpPost(salonId: $salonId, input: $input) {
      postName
    }
  }
`);

export const UPDATE_GBP_HOURS_MUTATION = gql(`
  mutation UpdateGbpHours($salonId: ID!, $weeklyHours: [GbpDayHoursInput!]!) {
    updateGbpHours(salonId: $salonId, weeklyHours: $weeklyHours)
  }
`);

export const UPLOAD_GBP_PHOTO_MUTATION = gql(`
  mutation UploadGbpPhoto($salonId: ID!, $input: UploadGbpPhotoInput!) {
    uploadGbpPhoto(salonId: $salonId, input: $input) {
      mediaName
    }
  }
`);

export const GBP_COVER_PHOTO_QUERY = gql(`
  query GbpCoverPhoto($salonId: ID!) {
    gbpCoverPhoto(salonId: $salonId) {
      photoUrl
    }
  }
`);

export const GBP_LIST_MEDIA_QUERY = gql(`
  query GbpListMedia($salonId: ID!) {
    gbpListMedia(salonId: $salonId) {
      mediaName
      category
      googleUrl
      thumbnailUrl
    }
  }
`);

export const GOOGLE_BUSINESS_INSIGHTS_QUERY = gql(`
  query GoogleBusinessInsights($salonId: ID!) {
    googleBusinessInsights(salonId: $salonId) {
      profileViews {
        current
        previous
        changePercent
      }
      searchImpressions {
        current
        previous
        changePercent
      }
      directionRequests {
        current
        previous
        changePercent
      }
      phoneCalls {
        current
        previous
        changePercent
      }
      currentPeriodStart
      currentPeriodEnd
      previousPeriodStart
      previousPeriodEnd
      profile {
        category
        phone
        website
        websiteUrl
        address
        weeklyHours {
          weekday
          open
          close
          closed
        }
      }
    }
  }
`);

export const TRIGGER_GOOGLE_LOCATION_SYNC_MUTATION = gql(`
  mutation TriggerGoogleLocationSync($salonId: ID!) {
    triggerGoogleLocationSync(salonId: $salonId)
  }
`);

export const SET_GOOGLE_BUSINESS_HOURS_SYNC_ENABLED_MUTATION = gql(`
  mutation SetGoogleBusinessHoursSyncEnabled($salonId: ID!, $enabled: Boolean!) {
    setGoogleBusinessHoursSyncEnabled(salonId: $salonId, enabled: $enabled) {
      id
      salonId
      syncHoursToGoogle
      lastHoursPushAt
      lastHoursPushError
    }
  }
`);

export const SET_GOOGLE_BUSINESS_HOLIDAYS_SYNC_ENABLED_MUTATION = gql(`
  mutation SetGoogleBusinessHolidaysSyncEnabled($salonId: ID!, $enabled: Boolean!) {
    setGoogleBusinessHolidaysSyncEnabled(salonId: $salonId, enabled: $enabled) {
      id
      salonId
      syncHolidaysToGoogle
      lastHolidaysPushAt
      lastHolidaysPushError
    }
  }
`);

export const SET_GOOGLE_BUSINESS_SERVICES_SYNC_ENABLED_MUTATION = gql(`
  mutation SetGoogleBusinessServicesSyncEnabled($salonId: ID!, $enabled: Boolean!) {
    setGoogleBusinessServicesSyncEnabled(salonId: $salonId, enabled: $enabled) {
      id
      salonId
      syncServicesToGoogle
      lastServicesPushAt
      lastServicesPushError
    }
  }
`);

export const TRIGGER_GBP_HOURS_PUSH_MUTATION = gql(`
  mutation TriggerGbpHoursPush($salonId: ID!) {
    triggerGbpHoursPush(salonId: $salonId)
  }
`);

export const TRIGGER_GBP_HOLIDAYS_PUSH_MUTATION = gql(`
  mutation TriggerGbpHolidaysPush($salonId: ID!) {
    triggerGbpHolidaysPush(salonId: $salonId)
  }
`);

export const TRIGGER_GBP_SERVICES_PUSH_MUTATION = gql(`
  mutation TriggerGbpServicesPush($salonId: ID!) {
    triggerGbpServicesPush(salonId: $salonId)
  }
`);

export const CHECK_SALON_AI_AVAILABILITY_QUERY = gql(`
  query CheckSalonAiAvailability($salonId: ID!) {
    checkSalonAiAvailability(salonId: $salonId) {
      hasCredits
      canUseAi
    }
  }
`);

export const BATCH_TRANSLATE_REVIEWS_MUTATION = gql(`
  mutation BatchTranslateReviews($salonId: ID!, $reviewIds: [ID!]!, $targetLanguage: String!) {
    batchTranslateReviews(salonId: $salonId, reviewIds: $reviewIds, targetLanguage: $targetLanguage) {
      translatedCount
      failedIds
      translations {
        id
        translatedComment
      }
    }
  }
`);

export const TRIGGER_GOOGLE_REVIEWS_SYNC_MUTATION = gql(`
  mutation TriggerGoogleReviewsSync($salonId: ID!) {
    triggerGoogleReviewsSync(salonId: $salonId)
  }
`);
